import React, { useState, useEffect, useCallback } from 'react';
import './AdminPanel.css';

function AdminPanel({ onClose, currentUser }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalOrganizations: 0,
    totalDiagnoses: 0,
    totalRevenue: 0,
    todayDiagnoses: 0,
    todayRevenue: 0,
    monthlyRevenue: 0,
    activeUsers: 0
  });
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [apiUsage, setApiUsage] = useState({ gpt4o: 0, gemini: 0, claude: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditAction, setCreditAction] = useState('add');
  const [message, setMessage] = useState({ type: '', text: '' });

  // Fetch all admin data
  const fetchAdminData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/dashboard', {
        headers: {
          'X-User-Phone': currentUser?.phoneNumber || ''
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setStats(data.stats);
        setUsers(data.users || []);
        setOrganizations(data.organizations || []);
        setPayments(data.payments || []);
        setDiagnoses(data.diagnoses || []);
        setApiUsage(data.apiUsage || { gpt4o: 0, gemini: 0, claude: 0, total: 0 });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to load admin data' });
      }
    } catch (error) {
      console.error('Admin fetch error:', error);
      setMessage({ type: 'error', text: 'Failed to connect to server' });
    }
    setLoading(false);
  }, [currentUser]);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  // Handle credit management
  const handleCreditUpdate = async () => {
    if (!selectedUser || !creditAmount || isNaN(creditAmount)) {
      setMessage({ type: 'error', text: 'Please select a user and enter valid credit amount' });
      return;
    }

    try {
      const response = await fetch('/api/admin/credits', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Phone': currentUser?.phoneNumber || ''
        },
        body: JSON.stringify({
          userId: selectedUser.id,
          amount: parseInt(creditAmount),
          action: creditAction
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: `Successfully ${creditAction}ed ${creditAmount} credits` });
        setCreditAmount('');
        setSelectedUser(null);
        fetchAdminData();
      } else {
        setMessage({ type: 'error', text: data.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update credits' });
    }
  };

  // Handle user block/unblock
  const handleUserStatus = async (userId, action) => {
    try {
      const response = await fetch('/api/admin/user-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Phone': currentUser?.phoneNumber || ''
        },
        body: JSON.stringify({ userId, action })
      });
      
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: `User ${action}ed successfully` });
        fetchAdminData();
      } else {
        setMessage({ type: 'error', text: data.error });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update user status' });
    }
  };

  // Filter users based on search
  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone?.includes(searchTerm) ||
    user.organization?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-overlay">
      <div className="admin-panel">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <span className="admin-icon">⚙️</span>
            <h2>Platform Admin Panel</h2>
          </div>
          <button className="admin-close" onClick={onClose}>✕</button>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`admin-message ${message.type}`}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })}>✕</button>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="admin-tabs">
          <button 
            className={activeTab === 'dashboard' ? 'active' : ''} 
            onClick={() => setActiveTab('dashboard')}
          >
            📊 Dashboard
          </button>
          <button 
            className={activeTab === 'users' ? 'active' : ''} 
            onClick={() => setActiveTab('users')}
          >
            👥 Users
          </button>
          <button 
            className={activeTab === 'organizations' ? 'active' : ''} 
            onClick={() => setActiveTab('organizations')}
          >
            🏥 Organizations
          </button>
          <button 
            className={activeTab === 'revenue' ? 'active' : ''} 
            onClick={() => setActiveTab('revenue')}
          >
            💰 Revenue
          </button>
          <button 
            className={activeTab === 'diagnoses' ? 'active' : ''} 
            onClick={() => setActiveTab('diagnoses')}
          >
            🩺 Diagnoses
          </button>
          <button 
            className={activeTab === 'credits' ? 'active' : ''} 
            onClick={() => setActiveTab('credits')}
          >
            🪙 Credits
          </button>
          <button 
            className={activeTab === 'api' ? 'active' : ''} 
            onClick={() => setActiveTab('api')}
          >
            🤖 API Usage
          </button>
        </div>

        {/* Content */}
        <div className="admin-content">
          {loading ? (
            <div className="admin-loading">
              <div className="spinner"></div>
              <p>Loading admin data...</p>
            </div>
          ) : (
            <>
              {/* Dashboard Tab */}
              {activeTab === 'dashboard' && (
                <div className="dashboard-tab">
                  <div className="stats-grid">
                    <div className="stat-card blue">
                      <div className="stat-icon">👥</div>
                      <div className="stat-info">
                        <h3>{stats.totalUsers}</h3>
                        <p>Total Users</p>
                      </div>
                    </div>
                    <div className="stat-card green">
                      <div className="stat-icon">🏥</div>
                      <div className="stat-info">
                        <h3>{stats.totalOrganizations}</h3>
                        <p>Organizations</p>
                      </div>
                    </div>
                    <div className="stat-card purple">
                      <div className="stat-icon">🩺</div>
                      <div className="stat-info">
                        <h3>{stats.totalDiagnoses}</h3>
                        <p>Total Diagnoses</p>
                      </div>
                    </div>
                    <div className="stat-card orange">
                      <div className="stat-icon">💰</div>
                      <div className="stat-info">
                        <h3>{formatCurrency(stats.totalRevenue)}</h3>
                        <p>Total Revenue</p>
                      </div>
                    </div>
                  </div>

                  <div className="stats-row">
                    <div className="stat-card-small">
                      <h4>Today's Diagnoses</h4>
                      <p className="stat-number">{stats.todayDiagnoses}</p>
                    </div>
                    <div className="stat-card-small">
                      <h4>Today's Revenue</h4>
                      <p className="stat-number">{formatCurrency(stats.todayRevenue)}</p>
                    </div>
                    <div className="stat-card-small">
                      <h4>Monthly Revenue</h4>
                      <p className="stat-number">{formatCurrency(stats.monthlyRevenue)}</p>
                    </div>
                    <div className="stat-card-small">
                      <h4>Active Users (30d)</h4>
                      <p className="stat-number">{stats.activeUsers}</p>
                    </div>
                  </div>

                  <div className="recent-activity">
                    <h3>Recent Activity</h3>
                    <div className="activity-list">
                      {diagnoses.slice(0, 5).map((diag, index) => (
                        <div key={index} className="activity-item">
                          <span className="activity-icon">🩺</span>
                          <span className="activity-text">
                            {diag.userName || 'User'} created a diagnosis
                          </span>
                          <span className="activity-time">{formatDate(diag.createdAt)}</span>
                        </div>
                      ))}
                      {payments.slice(0, 3).map((pay, index) => (
                        <div key={`pay-${index}`} className="activity-item">
                          <span className="activity-icon">💳</span>
                          <span className="activity-text">
                            Payment of {formatCurrency(pay.amount)} received
                          </span>
                          <span className="activity-time">{formatDate(pay.createdAt)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <div className="users-tab">
                  <div className="tab-header">
                    <h3>All Users ({users.length})</h3>
                    <input
                      type="text"
                      placeholder="Search by name, phone, or organization..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                  
                  <div className="table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Phone</th>
                          <th>Organization</th>
                          <th>Credits</th>
                          <th>Diagnoses</th>
                          <th>Joined</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map(user => (
                          <tr key={user.id}>
                            <td>{user.name || 'N/A'}</td>
                            <td>{user.phone}</td>
                            <td>{user.organization || 'Individual'}</td>
                            <td>
                              <span className="credit-badge">
                                {user.creditsRemaining || 0}
                              </span>
                            </td>
                            <td>{user.diagnosesCount || 0}</td>
                            <td>{formatDate(user.createdAt)}</td>
                            <td>
                              <span className={`status-badge ${user.blocked ? 'blocked' : 'active'}`}>
                                {user.blocked ? 'Blocked' : 'Active'}
                              </span>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <button 
                                  className="btn-small btn-credit"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setActiveTab('credits');
                                  }}
                                >
                                  💳
                                </button>
                                {user.blocked ? (
                                  <button 
                                    className="btn-small btn-unblock"
                                    onClick={() => handleUserStatus(user.id, 'unblock')}
                                  >
                                    ✓
                                  </button>
                                ) : (
                                  <button 
                                    className="btn-small btn-block"
                                    onClick={() => handleUserStatus(user.id, 'block')}
                                  >
                                    🚫
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Organizations Tab */}
              {activeTab === 'organizations' && (
                <div className="organizations-tab">
                  <div className="tab-header">
                    <h3>All Organizations ({organizations.length})</h3>
                  </div>
                  
                  <div className="table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Organization Name</th>
                          <th>Total Users</th>
                          <th>Total Credits</th>
                          <th>Credits Used</th>
                          <th>Diagnoses</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {organizations.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="empty-state">No organizations found</td>
                          </tr>
                        ) : (
                          organizations.map(org => (
                            <tr key={org.id}>
                              <td>{org.name}</td>
                              <td>{org.userCount || 0}</td>
                              <td>{org.creditsTotal || 0}</td>
                              <td>{org.creditsUsed || 0}</td>
                              <td>{org.diagnosesCount || 0}</td>
                              <td>{formatDate(org.createdAt)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Revenue Tab */}
              {activeTab === 'revenue' && (
                <div className="revenue-tab">
                  <div className="revenue-summary">
                    <div className="revenue-card">
                      <h4>Total Revenue</h4>
                      <p className="revenue-amount">{formatCurrency(stats.totalRevenue)}</p>
                    </div>
                    <div className="revenue-card">
                      <h4>This Month</h4>
                      <p className="revenue-amount">{formatCurrency(stats.monthlyRevenue)}</p>
                    </div>
                    <div className="revenue-card">
                      <h4>Today</h4>
                      <p className="revenue-amount">{formatCurrency(stats.todayRevenue)}</p>
                    </div>
                  </div>

                  <div className="tab-header">
                    <h3>Payment History</h3>
                  </div>
                  
                  <div className="table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>User</th>
                          <th>Plan</th>
                          <th>Amount</th>
                          <th>Credits</th>
                          <th>Payment ID</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="empty-state">No payments found</td>
                          </tr>
                        ) : (
                          payments.map(payment => (
                            <tr key={payment.id}>
                              <td>{formatDate(payment.createdAt)}</td>
                              <td>{payment.userName || payment.userPhone || 'N/A'}</td>
                              <td>{payment.planName}</td>
                              <td className="amount-cell">{formatCurrency(payment.amount)}</td>
                              <td>{payment.credits}</td>
                              <td className="payment-id">{payment.paymentId?.slice(0, 15)}...</td>
                              <td>
                                <span className="status-badge success">
                                  {payment.status || 'Success'}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Diagnoses Tab */}
              {activeTab === 'diagnoses' && (
                <div className="diagnoses-tab">
                  <div className="tab-header">
                    <h3>All Diagnoses ({diagnoses.length})</h3>
                  </div>
                  
                  <div className="table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>User</th>
                          <th>AI Models Used</th>
                          <th>Credits Used</th>
                          <th>Symptoms (Preview)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diagnoses.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="empty-state">No diagnoses found</td>
                          </tr>
                        ) : (
                          diagnoses.map(diag => (
                            <tr key={diag.id}>
                              <td>{formatDate(diag.createdAt)}</td>
                              <td>{diag.userName || diag.userPhone || 'N/A'}</td>
                              <td>{diag.modelsUsed || 'N/A'}</td>
                              <td>{diag.creditsUsed || 1}</td>
                              <td className="symptoms-preview">
                                {diag.symptoms?.slice(0, 50)}...
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Credits Tab */}
              {activeTab === 'credits' && (
                <div className="credits-tab">
                  <div className="credits-form">
                    <h3>Credit Management</h3>
                    <p className="form-description">Add or remove credits for any user manually</p>
                    
                    <div className="form-group">
                      <label>Select User</label>
                      <select 
                        value={selectedUser?.id || ''} 
                        onChange={(e) => {
                          const user = users.find(u => u.id === e.target.value);
                          setSelectedUser(user);
                        }}
                      >
                        <option value="">-- Select a user --</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name || user.phone} ({user.creditsRemaining || 0} credits)
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedUser && (
                      <div className="selected-user-info">
                        <p><strong>Name:</strong> {selectedUser.name || 'N/A'}</p>
                        <p><strong>Phone:</strong> {selectedUser.phone}</p>
                        <p><strong>Current Credits:</strong> {selectedUser.creditsRemaining || 0}</p>
                      </div>
                    )}

                    <div className="form-row">
                      <div className="form-group">
                        <label>Action</label>
                        <select 
                          value={creditAction} 
                          onChange={(e) => setCreditAction(e.target.value)}
                        >
                          <option value="add">Add Credits</option>
                          <option value="remove">Remove Credits</option>
                          <option value="set">Set Credits To</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Amount</label>
                        <input
                          type="number"
                          value={creditAmount}
                          onChange={(e) => setCreditAmount(e.target.value)}
                          placeholder="Enter credit amount"
                          min="0"
                        />
                      </div>
                    </div>

                    <button 
                      className="btn-primary"
                      onClick={handleCreditUpdate}
                      disabled={!selectedUser || !creditAmount}
                    >
                      {creditAction === 'add' ? '➕ Add Credits' : 
                       creditAction === 'remove' ? '➖ Remove Credits' : 
                       '🔄 Set Credits'}
                    </button>
                  </div>

                  <div className="credits-history">
                    <h3>Users by Credits</h3>
                    <div className="table-container">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Phone</th>
                            <th>Total Credits</th>
                            <th>Used</th>
                            <th>Remaining</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.sort((a, b) => (b.creditsRemaining || 0) - (a.creditsRemaining || 0)).map(user => (
                            <tr key={user.id}>
                              <td>{user.name || 'N/A'}</td>
                              <td>{user.phone}</td>
                              <td>{user.creditsTotal || 0}</td>
                              <td>{user.creditsUsed || 0}</td>
                              <td>
                                <span className={`credit-badge ${(user.creditsRemaining || 0) < 5 ? 'low' : ''}`}>
                                  {user.creditsRemaining || 0}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* API Usage Tab */}
              {activeTab === 'api' && (
                <div className="api-tab">
                  <div className="api-summary">
                    <h3>API Usage & Costs</h3>
                    <p className="form-description">Estimated API costs based on usage</p>
                    
                    <div className="api-cards">
                      <div className="api-card gpt">
                        <div className="api-icon">🟢</div>
                        <div className="api-info">
                          <h4>GPT-4o (AI Engine A)</h4>
                          <p className="api-calls">{apiUsage.gpt4o} calls</p>
                          <p className="api-cost">Est. Cost: {formatCurrency(apiUsage.gpt4o * 2.5)}</p>
                        </div>
                      </div>
                      <div className="api-card gemini">
                        <div className="api-icon">🔵</div>
                        <div className="api-info">
                          <h4>Gemini (AI Engine B)</h4>
                          <p className="api-calls">{apiUsage.gemini} calls</p>
                          <p className="api-cost">Est. Cost: {formatCurrency(apiUsage.gemini * 0.5)}</p>
                        </div>
                      </div>
                      <div className="api-card claude">
                        <div className="api-icon">🟣</div>
                        <div className="api-info">
                          <h4>Claude (AI Engine C)</h4>
                          <p className="api-calls">{apiUsage.claude} calls</p>
                          <p className="api-cost">Est. Cost: {formatCurrency(apiUsage.claude * 15)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="api-total">
                      <div className="total-box">
                        <h4>Total API Calls</h4>
                        <p className="total-number">{apiUsage.total}</p>
                      </div>
                      <div className="total-box">
                        <h4>Total Estimated Cost</h4>
                        <p className="total-number cost">
                          {formatCurrency(
                            (apiUsage.gpt4o * 2.5) + 
                            (apiUsage.gemini * 0.5) + 
                            (apiUsage.claude * 15)
                          )}
                        </p>
                      </div>
                      <div className="total-box">
                        <h4>Revenue - Cost</h4>
                        <p className="total-number profit">
                          {formatCurrency(
                            stats.totalRevenue - 
                            ((apiUsage.gpt4o * 2.5) + (apiUsage.gemini * 0.5) + (apiUsage.claude * 15))
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="api-note">
                      <p>💡 <strong>Note:</strong> Costs are estimates based on average API pricing:</p>
                      <ul>
                        <li>GPT-4o: ~₹2.50 per call</li>
                        <li>Gemini: ~₹0.50 per call</li>
                        <li>Claude Opus: ~₹15.00 per call</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="admin-footer">
          <button className="btn-refresh" onClick={fetchAdminData}>
            🔄 Refresh Data
          </button>
          <span className="admin-version">LatDiagnostics Admin v1.0</span>
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
